import { FunctionalComponent, h } from "vue"
import { IconSet, IconProps, IconAliases } from "vuetify"
import { aliases as mdiAliases } from "vuetify/iconsets/mdi-svg"
import { VSvgIcon } from "vuetify/lib/composables/icons.mjs"
import { isSvgPath } from "@/helpers/stringHelpers"
import {
  IconName,
  CustomIcons,
  FlipModifier,
  RotationModifier,
  AnimationModifier,
  SizeModifier,
} from "@/types/Icon"
import {
  SIZE_CLASSES,
  ANIMATION_CLASSES,
  ROTATION_CLASSES,
  FLIP_CLASSES,
} from "@/constants/icon"

import {
  Zap,
  Flame,
  X,
  LayoutDashboard,
  Mail,
  ChartColumn,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  CircleCheck,
  CircleX,
  Info,
  Wrench,
  CircleAlert,
  SquarePen,
  MessageSquareMore,
  List,
  File,
  Wallet,
  User,
  UserPlus,
  UserSearch,
  Copy,
  Gauge,
  Radio,
  House,
  FileText,
  ListOrdered,
  Bold,
  Italic,
  Underline,
  Highlighter,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  TextQuote,
  Code,
  Undo,
  Redo,
  Filter,
  CircleOff,
  Search,
  Equal,
  Network,
  ListCheck,
  Microchip,
  AtSign,
  Send,
  Plus,
  SquarePlus,
  Trash,
  ArrowRightFromLine,
  ArrowLeftFromLine,
  Settings,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  FileImage,
  FileMusic,
  FileVideo,
  Monitor,
  Phone,
  MessageCircle,
  ClipboardCheck,
  AlertTriangle,
  Lightbulb,
  Folder,
  History,
  Pencil,
  Ellipsis,
  Play,
  Download,
  ChevronLeft,
  ChevronRight,
  Upload,
  PersonStanding,
  FilePen,
  FileDown,
  Save,
  CloudDownload,
  DatabaseBackup,
  Minus,
  Check,
  ArrowUpRight,
  FileQuestion,
  Calendar,
  StickyNote,
  Camera,
  Calculator,
  LogOut,
  Bomb,
  Ban,
  Sun,
  MoonStar,
  Lock,
  LockOpen,
  KeyRound,
  Repeat2,
  RefreshCwOff,
  BriefcaseBusiness,
  Clock,
  SmartphoneNfc,
  Binary,
  Eye,
  EyeOff,
  Car,
  GripVertical,
} from "lucide-vue-next"

import {
  mdiMapMarkerQuestionOutline,
  mdiTablet,
  mdiRocket,
  mdiTagSearch,
  mdiTagText,
  mdiToggleSwitchOffOutline,
  mdiBugOutline,
  mdiHelp,
  mdiHelpCircleOutline,
  mdiTwitter,
  mdiFacebook,
  mdiWhatsapp,
  mdiReact,
  mdiSync,
  mdiInformationVariantCircleOutline,
  mdiBroadcast,
  mdiArrowCollapse,
  mdiArrowExpand,
  mdiCircleSlice8,
  mdiContentDuplicate,
  mdiTagRemoveOutline,
  mdiTuneVariant,
  mdiCircle,
  mdiOpenInNew,
  mdiTableCog,
  mdiUpdate,
  mdiFactory,
  mdiHeadOutline,
} from "@mdi/js"

interface IconComponentProps extends IconProps {
  class?: string
  style?: Record<string, string | number>
}

/**
 * Collection of custom icons mapped to their respective components.
 * Each key represents an icon name that can be used throughout the application,
 * and each value is the corresponding icon component (SVG or Functional component).
 */
export const customIcons: CustomIcons = {
  // A
  alert: AlertTriangle,
  "alert-circle": CircleAlert,
  "align-center": AlignCenter,
  "align-left": AlignLeft,
  "align-right": AlignRight,
  "arrow-down": ArrowDown,
  "arrow-expand-left": ArrowLeftFromLine,
  "arrow-expand-right": ArrowRightFromLine,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up": ArrowUp,
  "arrow-up-right": ArrowUpRight,
  "at-sign": AtSign,

  // B
  binary: Binary,
  bold: Bold,
  bomb: Bomb,
  briefcase: BriefcaseBusiness,
  broadcast: mdiBroadcast,
  bug: mdiBugOutline,

  // C
  calculator: Calculator,
  calendar: Calendar,
  camera: Camera,
  cancel: Ban,
  car: Car,
  chart: ChartColumn,
  check: Check,
  "check-circle": CircleCheck,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  circle: mdiCircle,
  "circle-active": mdiCircleSlice8,
  "circle-off": CircleOff,
  "clipboard-check": ClipboardCheck,
  clock: Clock,
  close: X,
  "close-circle": CircleX,
  "cloud-download": CloudDownload,
  code: Code,
  cog: Settings,
  collapse: mdiArrowCollapse,
  "content-convert": mdiContentDuplicate,
  copy: Copy,

  // D
  "database-sync": DatabaseBackup,
  delete: Trash,
  download: Download,

  // E
  electric: Zap,
  ellipsis: Ellipsis,
  email: Mail,
  equal: Equal,
  expand: mdiArrowExpand,
  "external-link": mdiOpenInNew,
  eye: Eye,
  "eye-off": EyeOff,

  // F
  facebook: mdiFacebook,
  factory: mdiFactory,
  file: File,
  "file-download": FileDown,
  "file-edit": FilePen,
  "file-image": FileImage,
  "file-music": FileMusic,
  "file-question": FileQuestion,
  "file-text": FileText,
  "file-video": FileVideo,
  filter: Filter,
  "fine-tune": mdiTuneVariant,
  folder: Folder,

  // G
  gas: Flame,
  "grip-vertical": GripVertical,

  // H
  head: mdiHeadOutline,
  help: mdiHelp,
  "help-circle": mdiHelpCircleOutline,
  highlight: Highlighter,
  history: History,
  home: House,
  human: PersonStanding,

  // I
  info: Info,
  "info-variant": mdiInformationVariantCircleOutline,
  italic: Italic,

  // K
  key: KeyRound,

  // L
  lightbulb: Lightbulb,
  link: Link,
  list: List,
  "list-check": ListCheck,
  "list-ordered": ListOrdered,
  lock: Lock,
  "lock-open": LockOpen,
  logout: LogOut,

  // M
  "map-marker-question": mdiMapMarkerQuestionOutline,
  message: MessageCircle,
  "message-processing": MessageSquareMore,
  meter: Gauge,
  microchip: Microchip,
  minus: Minus,
  monitor: Monitor,

  // N
  network: Network,
  note: StickyNote,

  // O
  overview: LayoutDashboard,

  // P
  pencil: Pencil,
  phone: Phone,
  play: Play,
  plus: Plus,

  // R
  react: mdiReact,
  redo: Redo,
  refresh: RefreshCw,
  "refresh-off": RefreshCwOff,
  repeat: Repeat2,
  rocket: mdiRocket,

  // S
  save: Save,
  search: Search,
  send: Send,
  "signal-box": SmartphoneNfc,
  signals: Radio,
  "square-edit": SquarePen,
  "square-plus": SquarePlus,
  switch: mdiToggleSwitchOffOutline,
  sync: mdiSync,

  // T
  "table-edit": mdiTableCog,
  tablet: mdiTablet,
  "tag-remove": mdiTagRemoveOutline,
  "tag-search": mdiTagSearch,
  "tag-text": mdiTagText,
  "text-quote": TextQuote,
  twitter: mdiTwitter,

  // U
  underline: Underline,
  undo: Undo,
  update: mdiUpdate,
  upload: Upload,
  user: User,
  "user-plus": UserPlus,
  "user-search": UserSearch,

  // W
  wallet: Wallet,
  "weather-night": MoonStar,
  "weather-sunny": Sun,
  whatsapp: mdiWhatsapp,
  wrench: Wrench,
}

const aliases: IconAliases = {
  ...mdiAliases,
  close: "close",
  calendar: "calendar",
}

/**
 * Parse an icon string into icon name and modifiers
 * @param iconString - The icon string to parse (e.g. "alert rotate-90 lg")
 * @returns Object containing iconName and modifiers array
 */
const parseIconString = (
  iconString: string,
): { iconName: string; modifiers: string[] } => {
  const parts = iconString.trim().split(/\s+/)
  const iconName = parts[0]
  const modifiers = parts.slice(1)

  return { iconName, modifiers }
}

/**
 * Generate inline styles based on provided modifiers
 * @param modifiers - Array of modifier strings
 * @returns Object containing CSS styles
 */
const generateInlineStyles = (
  modifiers: string[],
): Record<string, string | number> => {
  const styles: Record<string, string | number> = {}

  modifiers.forEach((modifier) => {
    if (modifier in SIZE_CLASSES) {
      Object.assign(styles, SIZE_CLASSES[modifier as SizeModifier])
    }
    if (modifier in ANIMATION_CLASSES) {
      Object.assign(styles, ANIMATION_CLASSES[modifier as AnimationModifier])
    }
    if (modifier in ROTATION_CLASSES) {
      Object.assign(styles, ROTATION_CLASSES[modifier as RotationModifier])
    }
    if (modifier in FLIP_CLASSES) {
      Object.assign(styles, FLIP_CLASSES[modifier as FlipModifier])
    }
  })

  return styles
}

const sets: Record<string, IconSet> = {
  custom: {
    component: (props: IconComponentProps): ReturnType<typeof h> | null => {
      const {
        icon: iconProp,
        class: className,
        style: styleProp = {},
        ...restProps
      } = props

      if (typeof iconProp !== "string") {
        console.warn(`Expected string icon but got ${typeof iconProp}`)
        return null
      }

      const { iconName, modifiers } = parseIconString(iconProp)
      const modifierStyles = generateInlineStyles(modifiers)
      const combinedStyles = { ...(styleProp || {}), ...modifierStyles }

      if (iconName in customIcons) {
        const iconValue = customIcons[iconName as IconName]

        if (isSvgPath(iconValue)) {
          return h(VSvgIcon, {
            ...restProps,
            icon: iconValue,
            class: className,
            style: combinedStyles,
          })
        }

        return h(iconValue as FunctionalComponent, {
          ...restProps,
          class: className,
          style: combinedStyles,
        })
      }

      console.warn(`Icon "${iconName}" not found`)
      return null
    },
  },
}

const defaultSet: keyof typeof sets = "custom"

export { aliases, sets, defaultSet }
